import React, {
  createContext, useContext, useEffect, useMemo, useRef,
} from 'react';
import { useQuery, ApolloQueryResult } from '@apollo/client';
import { GET_HUB_PET_PARENT, GET_PET_PARENT } from '../../queries';
import { PetParent } from '../../types/PetParent';

interface HubPetParentContextValue {
  petParent: PetParent | null;
  loading: boolean;
  refetch: () => Promise<ApolloQueryResult<{ petParent: PetParent }>>;
}

const HubPetParentContext = createContext<HubPetParentContextValue | null>(
  null,
);

export function HubPetParentProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    data, loading, error, refetch,
  } = useQuery<{ petParent: PetParent }>(
    GET_HUB_PET_PARENT,
    // Render whatever resolved even if a non-critical field (e.g. the checklist
    // completion list) errors — a single failed resolver shouldn't blank the Hub.
    { errorPolicy: 'all' },
  );

  // A transient failure at mount (e.g. the authenticate-flow race, or a lambda
  // cold-start blip) caches a null pet parent for the whole session — the Hub then
  // renders "Hi, undefined" with default content. Retry once after a short delay.
  const retriedRef = useRef(false);
  const petParentMissing = !loading && (!!error || !data?.petParent);
  useEffect(() => {
    if (!petParentMissing || retriedRef.current) return undefined;
    retriedRef.current = true;
    const t = setTimeout(() => { refetch().catch(() => {}); }, 1500);
    return () => clearTimeout(t);
  }, [petParentMissing, refetch]);
  // const { data, loading, refetch } = useQuery<{ petParent: PetParent }>(
  //   GET_PET_PARENT,
  //   {
  //     variables: {
  //       id: 246,
  //       includePdfMonkeyUrls: false,
  //     },
  //   },
  // );

  const value = useMemo(
    () => ({
      petParent: data?.petParent ?? null,
      loading,
      refetch,
    }),
    [data?.petParent, loading, refetch],
  );

  return (
    <HubPetParentContext.Provider value={value}>
      {children}
    </HubPetParentContext.Provider>
  );
}

export function useHubPetParent() {
  const context = useContext(HubPetParentContext);
  if (!context) {
    throw new Error(
      'useHubPetParent must be used within a HubPetParentProvider',
    );
  }
  return context;
}

export default HubPetParentContext;
